/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  CostCenterFinderListItem, FormRootSchema,
  FormStateSchema,
} from '@gh/builder-shared';
import { BuilderPermission, PolicyReportingMethod } from '@gh/builder-shared-data';
import { DateTimeTypes, FormState, NumberTypes, updateSchema, whenCreateOrEditMode } from '@gh/common-shared';
import { FormMode, getControl, getControlValue, markControlAsDirty, setControlValue } from '@gh/core-ui';
import { SystemVarsManager } from '../../resources/model/system-vars.model';

@Injectable()
export class SystemVarsManagerFormState extends FormState<SystemVarsManager> {
  constructor(private userContext: BuilderUserContext,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  get policyReportingMethod(): PolicyReportingMethod {
    return getControlValue(this.group, 'policyReportingMethod');
  }

  get useFacility(): boolean {
    return getControlValue(this.group, 'useFacility');
  }

  get policyAnnualAmount(): number {
    return getControlValue(this.group, 'policyAnnualAmount') || 0;
  }

  get policyUncompleteAmount(): number {
    return getControlValue(this.group, 'policyUncompleteAmount') || 0;
  }

  get policyNumberOfPolicies(): number {
    return getControlValue(this.group, 'policyNumberOfPolicies') || 0;
  }

  get commissionCc(): CostCenterFinderListItem {
    return getControlValue(this.group, 'commissionCc');
  }

  get interestCc(): CostCenterFinderListItem {
    return getControlValue(this.group, 'interestCc');
  }

  setCommissionCc(cc: CostCenterFinderListItem): void {
    setControlValue(this.group, 'commissionCc', cc);
    markControlAsDirty(this.group, 'commissionCc');
  }

  setInterestCc(cc: CostCenterFinderListItem): void {
    setControlValue(this.group, 'interestCc', cc);
    markControlAsDirty(this.group, 'interestCc');
  }

  updateForm(): void {
    const { policyReportingMethod, useFacility } = this;
    updateSchema(getControl(this.group, 'policyAnnualAmount'), {
      required: useFacility,
    });
    updateSchema(getControl(this.group, 'policyUncompleteAmount'), {
      required: useFacility,
      visible: policyReportingMethod === PolicyReportingMethod.Fixed12Month,
    });
    updateSchema(getControl(this.group, 'policyNumberOfPolicies'), {
      required: useFacility,
      visible: policyReportingMethod === PolicyReportingMethod.Fixed12Month,
    });
  }

  protected buildSchema(details: SystemVarsManager, { root }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Manager.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      mode,
      optimisticLock: true,
      enabled: whenCreateOrEditMode(),
      children: {
        commissionCc: void 0,
        interestCc: void 0,
        salesProfitPct: { type: NumberTypes.Percents_2 },
        useFacility: void 0,
        excludeVariations: void 0,
        company: void 0,
        policyNumber: void 0,
        policyStartDate: { type: DateTimeTypes.ShortDate },
        policyReportingMethod: { required: true },
        policyAnnualAmount: { required: details.useFacility, type: NumberTypes.Price },
        policyUncompleteAmount: {
          required: details.useFacility,
          visible: details.policyReportingMethod === PolicyReportingMethod.Fixed12Month,
          type: NumberTypes.Price,
        },
        policyNumberOfPolicies: {
          required: details.useFacility,
          visible: details.policyReportingMethod === PolicyReportingMethod.Fixed12Month,
          type: NumberTypes.Count,
        },
      },
    });
  }
}
